import type { AutocompleteUserResultFilterType } from '@ms/yammer-graphql';
import createCachedSelector from 're-reselect';

import {
  isArticlePublisher,
  isEditPublisher,
  isGroupPublisher,
  isGroupQuestionPublisher,
  isGroupThreadStarterPublisher,
  isNetworkQuestionNewQuestionPublisher,
  isShareTeamsMeetingPublisher,
  isUserThreadStarterPublisher,
} from '../../state/publishers/types';
import type { Group, MentionsBarState, Publisher, User, YammerState } from '../../state/types';
import { findByIdThrows as findGroup, isNetworkQuestionGroup } from '../groups/selectors';
import { getTypeaheadResultsIncorporatingRecipients } from '../typeahead/selectors';
import { findById as findUser, getFulfilledUsers, isExternal, isMtoUser } from '../users/selectors';

import { findByIdThrows } from './findPublisherSelectors';
import {
  dashboardCreatePublisherId,
  homePublisherId,
  leaderPublisherId,
  shareToPublisherId,
  universalCreatePublisherId,
} from './getId';
import { isReplyToSharePointThread } from './publisherTypeSelectors';
import { getCampaignId, isEditingDraftGroupMessage, isEditingDraftUserMessage } from './publishersSelectors';

type GetNotifiedUsers = (state: YammerState, publisherId: Publisher['id']) => User[];
/**
 * @deprecated PERFORMANCE This selects a list of entity objects, and will return a new array instance
 * whenever the publisher or ANY fulfilled user is updated in the store. Array selectors should only
 * manage memoized lists of ids, then consumers should pass each id to a child component which can use
 * "field" selectors to select only the specific fields they require and manage their own memoization
 * to avoid unnecessary re-renders.
 */
export const getNotifiedUsers: GetNotifiedUsers = createCachedSelector(
  findByIdThrows,
  getFulfilledUsers,
  (publisher, users) => publisher.notifiedUserIds.map((id) => users[id]).filter(Boolean)
)((_state, id) => id);

type GetOriginalNotifiedUserIds = (state: YammerState, publisherId: Publisher['id']) => User['id'][];
export const getOriginalNotifiedUserIds: GetOriginalNotifiedUserIds = createCachedSelector(
  findByIdThrows,
  (publisher) => (isEditPublisher(publisher) ? publisher.originalNotifiedUserIds : [])
)((_state, id) => id);

type GetSelectedGroup = (state: YammerState, publisherId: Publisher['id']) => Group | undefined;
/**
 * @deprecated PERFORMANCE This selects an entity object which will force unnecessary re-renders.
 * This should be replaced by a `getSelectedGroupId` selector, or consumers can use `getPublisherField(publisherId, 'groupId')`.
 */
export const getSelectedGroup: GetSelectedGroup = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  if (!isGroupPublisher(publisher)) {
    return;
  }

  return publisher.groupId ? findGroup(state, publisher.groupId) : undefined;
};

type NeedsToPickGroup = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const needsToPickGroup: NeedsToPickGroup = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  if (!isGroupThreadStarterPublisher(publisher)) {
    return false;
  }

  return publisher.groupId == null;
};

type GetGroupIfSelectable = (state: YammerState, publisherId: Publisher['id']) => Group | undefined;
/**
 * @deprecated PERFORMANCE This selects an entity object which will force unnecessary re-renders.
 * This should be replaced by a selctor which only returns the group id.
 */
export const getGroupIfSelectable: GetGroupIfSelectable = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);
  const campaignId = getCampaignId(state, publisherId);
  const isCampaignPublisherId = !!campaignId;
  const isArticle = isArticlePublisher(publisher);
  const isHomePublisher = publisherId === homePublisherId;
  const isLeaderPublisher = publisherId === leaderPublisherId;
  const isShareToPublisher = publisherId === shareToPublisherId;
  const isUniversalCreatePublisher = publisherId === universalCreatePublisherId;
  const isDashboardCreatePublisher = publisherId === dashboardCreatePublisherId;
  const isShareTeamsMeetingPublisherType = isShareTeamsMeetingPublisher(publisher);
  const isNotSharedMessage = publisher.sharedMessageId != null;

  const isExpectedPublisher =
    isHomePublisher ||
    isLeaderPublisher ||
    isShareToPublisher ||
    isUniversalCreatePublisher ||
    isDashboardCreatePublisher ||
    isCampaignPublisherId ||
    isArticle ||
    isShareTeamsMeetingPublisherType ||
    isNotSharedMessage;

  if (!isExpectedPublisher) {
    return undefined;
  }

  return getSelectedGroup(state, publisherId);
};

type GetUserFromPublisher = (state: YammerState, publisherId: Publisher['id']) => User | undefined;
/**
 * @deprecated PERFORMANCE This selects an entity object which will force unnecessary re-renders.
 * This should be replaced by a selector which only returns the user id.
 */
export const getUserFromPublisher: GetUserFromPublisher = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  if (!isUserThreadStarterPublisher(publisher)) {
    return;
  }

  return findUser(state, publisher.userId);
};

type GetSelectedAudience = (state: YammerState, publisherId: Publisher['id']) => (User | Group)[];
export const getSelectedAudience: GetSelectedAudience = createCachedSelector(
  getNotifiedUsers,
  getGroupIfSelectable,
  (notifiedUsers, selectedGroup) => {
    if (selectedGroup == null) {
      return notifiedUsers;
    }

    return [selectedGroup, ...notifiedUsers];
  }
)((_state, id) => id);

type GetTypeaheadResultsExcludingSelected = (
  state: YammerState,
  publisherId: Publisher['id'],
  search: string,
  userResultFilters?: AutocompleteUserResultFilterType[]
) => User[];
export const getTypeaheadResultsExcludingSelected: GetTypeaheadResultsExcludingSelected = createCachedSelector(
  getSelectedAudience,
  (
    state: YammerState,
    publisherId: Publisher['id'],
    search: string,
    userResultFilters?: AutocompleteUserResultFilterType[]
  ) =>
    getTypeaheadResultsIncorporatingRecipients(state, publisherId, { search, includeUsers: true, userResultFilters }),
  (selectedAudience, typeaheadResults) => {
    const selectedAudienceIds = selectedAudience.map((audienceItem) => audienceItem.id);

    return typeaheadResults.users.filter((user) => !selectedAudienceIds.includes(user.id));
  }
)((_state, id) => id);

type HasSelectedAudience = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const hasSelectedAudience: HasSelectedAudience = (state, publisherId) =>
  getSelectedAudience(state, publisherId).length > 0;

type IsAnnouncement = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isAnnouncement: IsAnnouncement = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  if (
    !isGroupThreadStarterPublisher(publisher) &&
    !isUserThreadStarterPublisher(publisher) &&
    !isEditingDraftGroupMessage(state, publisherId) &&
    !isEditingDraftUserMessage(state, publisherId)
  ) {
    return false;
  }

  return publisher.isAnnouncement;
};

type HasAddedNewNotifiedExternalParticipants = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const hasAddedNewNotifiedExternalParticipants: HasAddedNewNotifiedExternalParticipants = (state, publisherId) =>
  getNotifiedUsers(state, publisherId).some((user) => user.isGuest);

type HasAddedOnlyMTOParticipants = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const hasAddedOnlyMTOParticipants: HasAddedOnlyMTOParticipants = (state, publisherId) =>
  getNotifiedUsers(state, publisherId).every((user) => isMtoUser(state, user.id));

type HasAddedNewExternalUsersToSharePointThread = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const hasAddedNewExternalUsersToSharePointThread: HasAddedNewExternalUsersToSharePointThread = (
  state,
  publisherId
) => {
  if (!isReplyToSharePointThread(state, publisherId)) {
    return false;
  }

  return getNotifiedUsers(state, publisherId).some((user) => user.isGuest || isExternal(state, user.id));
};

type GetMentionsBarState = (state: YammerState, publisherId: Publisher['id']) => MentionsBarState;
export const getMentionsBarState: GetMentionsBarState = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);
  const isNetworkQuestionEnabled =
    isGroupQuestionPublisher(publisher) && isNetworkQuestionGroup(state, publisher.groupId);
  const shouldBeAlwaysVisible = isNetworkQuestionEnabled || !isNetworkQuestionNewQuestionPublisher(publisher);

  if (shouldBeAlwaysVisible) {
    return 'AlwaysVisible';
  }

  if (publisher.hasToggledMentionsBarToVisible) {
    return 'ToggledVisible';
  }

  return 'Hidden';
};
